<template>
  <div class="related-posts">
    <h3 class="section-title">{{ t("blog.relatedArticles") }}</h3>
    <div class="posts-grid">
      <BlogCard
        v-for="post in relatedPosts"
        :key="post.slug"
        :slug="post.slug"
        :title="post.title"
        :excerpt="post.excerpt"
        :image="post.image"
        :date="post.date"
        :categories="post.categories"
        class="related-post-card"
      />
    </div>
  </div>
</template>

<script setup>
import BlogCard from "./BlogCard.vue";
import blogPostsData from "~/data/blogPosts.json";

const { t } = useI18n();

const props = defineProps({
  currentSlug: {
    type: String,
    required: true,
  },
  currentCategories: {
    type: Array,
    default: () => [],
  },
  limit: {
    type: Number,
    default: 3,
  },
});

const { locale } = useI18n();

const relatedPosts = computed(() => {
  const others = blogPostsData.filter(
    (p) => p.slug !== props.currentSlug && p.lang === locale.value,
  );

  const scored = others.map((post) => {
    const shared = post.categories.filter((cat) =>
      props.currentCategories.includes(cat),
    ).length;
    return { ...post, _score: shared };
  });

  scored.sort((a, b) => {
    if (b._score !== a._score) return b._score - a._score;
    return new Date(b.date) - new Date(a.date);
  });

  return scored.slice(0, props.limit);
});
</script>

<style lang="scss" scoped>
.related-posts {
  font-family: $theme-font;
  margin: 1rem 0;
  padding-top: 3rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @include respond-below(sm) {
    margin: 3rem 0 2rem;
    padding-top: 2rem;
  }

  .section-title {
    font-family: $theme-font;
    font-size: $text-xl;
    font-weight: $fw-medium;
    color: rgba(255, 255, 255, 0.95);
    margin-bottom: 2rem;

    @include respond-below(sm) {
      font-size: $text-lg;
      margin-bottom: 1.5rem;
    }
  }

  .posts-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;

    @include respond-below(md) {
      grid-template-columns: repeat(2, 1fr);
    }

    @include respond-below(sm) {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .related-post-card {
      display: block;
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: $radius-md;
      overflow: hidden;
      transition: all 0.3s ease;
      text-decoration: none;

      &:hover {
        transform: translateY(-4px);
        border-color: rgba(95, 40, 213, 0.4);
        box-shadow: 0 8px 20px rgba(95, 40, 213, 0.15);

        .thumbnail {
          transform: scale(1.1);
        }
      }

      .image-wrapper {
        width: 100%;
        height: 180px;
        overflow: hidden;
        background: linear-gradient(160deg, #0093e9 0%, #80d0c7 100%);

        @include respond-below(sm) {
          height: 200px;
        }

        .thumbnail {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
      }

      .content {
        padding: 1.25rem;

        @include respond-below(sm) {
          padding: 1rem;
        }

        .categories {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
          flex-wrap: wrap;

          .category-tag {
            padding: 0.25rem 0.625rem;
            background: rgba(95, 40, 213, 0.2);
            border: 1px solid rgba(95, 40, 213, 0.3);
            border-radius: $radius-xxxl;
            font-size: $text-xxxs;
            color: rgba(255, 255, 255, 0.8);
            font-weight: $fw-medium;
          }
        }

        .title {
          font-family: $theme-font;
          font-size: $text-sm;
          font-weight: $fw-medium;
          line-height: 1.4;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 0.5rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .excerpt {
          font-family: $theme-font;
          font-size: $text-xxs;
          line-height: 1.5;
          color: rgba(255, 255, 255, 0.6);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      }
    }
  }
}
</style>
